import {
  addMonths,
  differenceInCalendarMonths,
  endOfDay,
  formatISO,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isValid,
  startOfDay,
} from 'date-fns';
import isEqual from 'lodash/isEqual';

import type { RootState } from '@/store';

import { selectCruisesCommonVoyagesData } from '@/ducks/cruisesCommon/selectors';
import { FILTER_KEY, type FiltersConfigValue, type FiltersTypes } from '@/ducks/filters/types';
import { getFiltersOptions } from '@/ducks/filtersOptions';
import { selectMnvvVoyage } from '@/ducks/mnvv/selectors';
import { calculateMaxDate } from '@/ducks/mnvv/utils';
import checkFlowType from '@/features/flowType/checkFlowType';
import { format, parse } from '@/helpers/util/dateUtil';

import { getSearchParamsValue } from '../utils/primitive';

type RawDate = null | string;

const checkIsSameYearAndMonth = (date1?: Date | null, date2?: Date | null) =>
  // the rest pods could send toDate near maxDate, like toDate=2025-04-30 when maxDate=2025-04-22
  date1 && date2 ? getYear(date1) === getYear(date2) && getMonth(date1) === getMonth(date2) : false;

const fitDateInMinMaxPeriod = (date: RawDate, minDate: Date | null, maxDate: Date | null) => {
  const d = parse(date);

  if (minDate && (!d || !isValid(d) || isBefore(d, minDate))) {
    return minDate;
  }

  if (maxDate && d && isAfter(d, maxDate)) {
    return maxDate;
  }

  return isValid(d) ? d : null;
};

export const getMinMaxDatePeriod = (state: RootState, searchParams: URLSearchParams) => {
  const { isMNVV } = checkFlowType(searchParams);
  const options = getFiltersOptions(state);
  const voyagesData = selectCruisesCommonVoyagesData(state);
  const voyage = selectMnvvVoyage(state);

  let minDate = parse(voyagesData.minDate);
  let maxDate = options.maxDate ? parse(options.maxDate) : null;

  if (isMNVV && voyage?.bookedDate) {
    maxDate = calculateMaxDate(voyage?.bookedDate) || maxDate;
  }

  if (minDate && isValid(minDate)) {
    minDate = startOfDay(minDate);
  }

  if (maxDate && isValid(maxDate)) {
    maxDate = endOfDay(maxDate);
  }

  return { maxDate: isValid(maxDate) ? maxDate! : null, minDate: isValid(minDate) ? minDate! : null };
};

export const isOutOfBounds = (
  dateFrom: string | null,
  dateTo: string | null,
  minDate: Date | string | null,
  maxDate: Date | string | null,
) => {
  // Compare dates only by string representation (To avoid possible errors in TZs due to different date parsing ways)
  const rangeMin = minDate instanceof Date ? formatISO(minDate, { representation: 'date' }) : minDate;
  const rangeMax = maxDate instanceof Date ? formatISO(maxDate, { representation: 'date' }) : maxDate;
  return Boolean(
    (dateFrom && minDate && isBefore(dateFrom, rangeMin)) || (dateTo && maxDate && isAfter(dateTo, rangeMax)),
  );
};

const getDatesPeriod = (
  state: RootState,
  searchParams: URLSearchParams,
  fromDate: null | string,
  toDate: null | string,
) => {
  const { maxDate, minDate } = getMinMaxDatePeriod(state, searchParams);

  const options = getFiltersOptions(state);

  const from = fitDateInMinMaxPeriod(fromDate || options.defaultStartDate, minDate, maxDate);
  let to = fitDateInMinMaxPeriod(toDate || options.defaultEndDate, minDate, maxDate);

  if (from && to) {
    if ((!isBefore(from, to) && !isEqual(from, to)) || (fromDate && !toDate)) {
      const parsedDefaultEndDate = options.defaultEndDate && parse(options.defaultEndDate);
      const parsedDefaultStartDate = options.defaultStartDate && parse(options.defaultStartDate);
      const defaultDateLength =
        isValid(parsedDefaultEndDate) && isValid(parsedDefaultStartDate)
          ? differenceInCalendarMonths(parsedDefaultEndDate as Date, parsedDefaultStartDate as Date)
          : 0;
      const parsedMaxDate = maxDate && parse(maxDate);
      const parsedFrom = from && parse(from);
      const diffFromMaxDate =
        isValid(parsedMaxDate) && isValid(parsedFrom) ? differenceInCalendarMonths(parsedMaxDate!, parsedFrom!) : 0;

      to = diffFromMaxDate > defaultDateLength ? format(addMonths(from, defaultDateLength)) : maxDate;
    }

    if (checkIsSameYearAndMonth(to, maxDate)) {
      to = maxDate;
    }

    return { fromDate: format(from) as string, toDate: format(to) as string };
  }

  return {
    fromDate: options.defaultStartDate || null,
    toDate: options.defaultEndDate || null,
  };
};

const fromDate: FiltersConfigValue<FiltersTypes['dateFrom']> = {
  getValue: ({ searchParams, state, value }) => {
    return getDatesPeriod(
      state,
      searchParams,
      value as string,
      getSearchParamsValue(FILTER_KEY.dateTo, searchParams) as string,
    ).fromDate;
  },
};

const toDate: FiltersConfigValue<FiltersTypes['dateTo']> = {
  getValue: ({ searchParams, state, value }) => {
    return getDatesPeriod(
      state,
      searchParams,
      getSearchParamsValue(FILTER_KEY.dateFrom, searchParams) as string,
      value as string,
    ).toDate;
  },
};

export { fromDate, toDate };
